/**
 * <infinite-product-list section-id="...">
 *
 * Appends the next page of products when the sentinel scrolls into view, using the Section
 * Rendering API (?section_id=). No "Load more" button and no page reload — the shopper just
 * keeps scrolling, which is the behaviour the store owner asked for.
 *
 * Expected markup inside the element:
 *   [data-infinite-target]    the container whose children are product cards
 *   [data-infinite-sentinel]  wrapper rendered only while paginate.next exists; it holds a
 *                             real <a rel="next"> so the collection stays crawlable and
 *                             usable with JavaScript disabled. CSS hides that link once this
 *                             element is :defined.
 *
 * Filters and sort are untouched: <product-facet> replaces #facet-main wholesale on those
 * events, which destroys and re-creates this element, so it naturally resets to page 1.
 */
/*
 * Scoped so the file can be loaded more than once. Three sale-page sections on one
 * page each request this script, and a top-level `class` binding makes the second
 * parse throw "already been declared" - which kills the whole script before the
 * customElements guard below is even reached.
 */
(() => {
class InfiniteProductList extends HTMLElement {
  connectedCallback() {
    this.target = this.querySelector('[data-infinite-target]');
    this.sectionId = this.getAttribute('section-id');

    if (!this.target || !this.sectionId || !('IntersectionObserver' in window)) {
      // Without the observer the <a rel="next"> stays visible and pagination still works.
      this.setAttribute('data-fallback', '');
      return;
    }

    this.loading = false;
    this.failures = 0;

    // Start fetching roughly one and a half screens early so the cards are in place before
    // the shopper reaches the bottom and there is no visible pause.
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
      },
      { rootMargin: '1200px 0px' }
    );

    this.observeSentinel();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.abortController?.abort();
    clearTimeout(this.retryTimer);
  }

  get sentinel() {
    return this.querySelector('[data-infinite-sentinel]');
  }

  observeSentinel() {
    const sentinel = this.sentinel;
    if (sentinel) this.observer.observe(sentinel);
  }

  async loadNext() {
    const sentinel = this.sentinel;
    if (!sentinel || this.loading) return;

    const link = sentinel.querySelector('a[rel="next"]');
    if (!link) return;

    this.loading = true;
    this.observer.unobserve(sentinel);
    sentinel.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);

      this.abortController = new AbortController();
      const response = await fetch(url.toString(), { signal: this.abortController.signal });
      if (!response.ok) throw new Error(`Section render failed: ${response.status}`);

      // DOMParser rather than innerHTML on a detached div: parsing into a separate document
      // keeps the browser from kicking off image requests for cards we have not inserted yet.
      const incoming = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incomingTarget = incoming.querySelector('[data-infinite-target]');
      const incomingSentinel = incoming.querySelector('[data-infinite-sentinel]');

      if (incomingTarget) {
        // Adopting the nodes upgrades <product-item> and friends against this document's
        // custom element registry, so quick-buy and colour swatches keep working.
        this.target.append(...Array.from(incomingTarget.children));
      }

      this.failures = 0;

      if (incomingSentinel) {
        sentinel.replaceWith(incomingSentinel);
        this.observer.observe(incomingSentinel);
      } else {
        sentinel.remove();
        this.observer.disconnect();
      }
    } catch (error) {
      if (error.name === 'AbortError') return;

      sentinel.removeAttribute('aria-busy');
      this.failures += 1;

      // No "Next" button: the store wants scroll-only loading. Wait a little longer after
      // each failure (2s, 4s, ... up to 10s) and watch the sentinel again, so the next scroll
      // near the bottom retries.
      const delay = Math.min(this.failures * 2000, 10000);
      this.retryTimer = setTimeout(() => {
        if (this.isConnected && this.sentinel) this.observer.observe(this.sentinel);
      }, delay);
    } finally {
      this.loading = false;
      this.sentinel?.removeAttribute('aria-busy');
    }
  }
}

if (!window.customElements.get('infinite-product-list')) {
  window.customElements.define('infinite-product-list', InfiniteProductList);
}

})();
